import * as React from "react";
import { Avatar, AvatarImage, AvatarFallback } from "./avatar";
import { Input } from "./input";
import { cn, getInitials } from "@/lib/utils";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";

export interface AvatarPreviewProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  fallbackText?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  inputClassName?: string;
  previewClassName?: string;
  id?: string;
  "aria-describedby"?: string;
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-12 w-12",
  lg: "h-16 w-16",
};

export function AvatarPreview({
  value,
  onChange,
  onBlur,
  disabled,
  placeholder = "https://example.com/avatar.jpg",
  fallbackText = "?",
  size = "md",
  className,
  inputClassName,
  previewClassName,
  id,
  "aria-describedby": ariaDescribedBy,
}: AvatarPreviewProps) {
  const { i18n } = useLingui();
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  // Reset error state when URL changes
  React.useEffect(() => {
    if (value) {
      setIsLoading(true);
      setHasError(false);
    } else {
      setIsLoading(false);
      setHasError(false);
    }
  }, [value]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };


  return (
    <div className={cn("flex items-center gap-4", className)}>
      <div className={cn("flex-1", inputClassName)}>
        <Input
          id={id}
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
          aria-describedby={ariaDescribedBy}
          className="w-full"
        />
      </div>
      
      <div className={cn("flex-shrink-0", previewClassName)}>
        <Avatar className={cn(sizeClasses[size], "border-2 border-muted")}>
          {value && !hasError ? (
            <AvatarImage
              src={value}
              alt={i18n._(msg`imageUpload.preview`)}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          ) : null}
          <AvatarFallback 
            className={cn(
              "text-xs font-medium",
              isLoading && "animate-pulse",
              hasError && "bg-destructive/10 text-destructive"
            )}
          >
            {isLoading ? "..." : (getInitials(fallbackText || "") || "?")}
          </AvatarFallback>
        </Avatar>
      </div>
    </div>
  );
}